/**
 * テロップで使えるフォントの許可リスト。
 *
 * Ported from video-editor repo (src/lib/captionFonts.ts) — 無改変コピー。
 * バックエンドの AI 提案プロンプト (backend/services/video_edit/propose_service.py)
 * にも同じ許可リスト・分類・説明文・提供ウェイトを複製しており、変更時は両方を更新すること
 * (docs/features/video-editor-ai-caption-design-catalog-plan.md の P3 でカタログ JSON の生成物に置き換える予定)。
 *
 * すべて Google Fonts の Web フォント (大半は OFL、Kosugi / Kosugi Maru のみ Apache License 2.0。
 * どちらも再配布可)。ブラウザのプレビュー (Remotion Player)
 * とサーバーレンダリング (Cloud Run 上の headless Chromium) が同じフォントファイルを
 * @remotion/google-fonts 経由で読み込むため、端末やコンテナのインストール状況に関係なく
 * プレビューと書き出しの見た目が一致する。macOS 標準フォント (Hiragino 等) はライセンス上
 * サーバーに同梱できないため使わない (旧データの名前は LEGACY_FAMILY_MAP で読み替える)。
 *
 * このファイル自体は remotion に依存しない。フォントの実体を読み込むのは
 * レンダラー側 (remotion/captionFontLoader.ts) だけ。
 */

export const CAPTION_FONT_FAMILIES = [
  // ゴシック
  'Noto Sans JP',
  'Zen Kaku Gothic New',
  'Zen Kaku Gothic Antique',
  'IBM Plex Sans JP',
  'LINE Seed JP',
  'M PLUS 2',
  'M PLUS 1p',
  'M PLUS U',
  'M PLUS 1 Code',
  'BIZ UDPGothic',
  'Sawarabi Gothic',
  'Kosugi',
  // 明朝
  'Noto Serif JP',
  'Shippori Mincho',
  'Shippori Mincho B1',
  'Zen Old Mincho',
  'Zen Antique',
  'Zen Antique Soft',
  'Shippori Antique',
  'Shippori Antique B1',
  'BIZ UDPMincho',
  'Sawarabi Mincho',
  'Hina Mincho',
  'New Tegomin',
  // 丸ゴシック
  'M PLUS Rounded 1c',
  'Zen Maru Gothic',
  'Kosugi Maru',
  'Kiwi Maru',
  // 手書き
  'Klee One',
  'Yusei Magic',
  'Yomogi',
  'Zen Kurenaido',
  'Hachi Maru Pop',
  'Yuji Boku',
  'Yuji Mai',
  'Yuji Syuku',
  // デザイン
  'Dela Gothic One',
  'Reggae One',
  'Mochiy Pop P One',
  'RocknRoll One',
  'Rampart One',
  'Train One',
  'Potta One',
  'Stick',
  'DotGothic16',
  'WDXL Lubrifont JP N',
  'Kaisei Decol',
  'Kaisei HarunoUmi',
  'Kaisei Opti',
  'Kaisei Tokumin',
  // 欧文
  'Anton',
  'Bebas Neue',
] as const;

export type CaptionFontFamilyName = (typeof CAPTION_FONT_FAMILIES)[number];

/**
 * プロジェクトのテロップが実際に使っている書体とウェイト
 * (xmlZip.collectFontUsages が作り、フォント ZIP の一覧・インストール確認で使う)。
 * ウェイトは resolveCaptionFontWeight で丸め済み (= XML の fontname に載る値)。
 */
export interface CaptionFontUsage {
  family: CaptionFontFamilyName;
  /** 使用ウェイト (昇順・重複なし)。 */
  weights: number[];
}

/** フォント一覧の分類。CAPTION_FONT_FAMILIES はこの順にまとまるよう並べてある。 */
export const CAPTION_FONT_CATEGORY_ORDER = [
  'ゴシック',
  '明朝',
  '丸ゴシック',
  '手書き',
  'デザイン',
  '欧文',
] as const;

export type CaptionFontCategory = (typeof CAPTION_FONT_CATEGORY_ORDER)[number];

/** 書体の分類 (フォント選択の見出しに使う)。 */
export const CAPTION_FONT_CATEGORIES: Record<CaptionFontFamilyName, CaptionFontCategory> = {
  // ゴシック
  'Noto Sans JP': 'ゴシック',
  'Zen Kaku Gothic New': 'ゴシック',
  'Zen Kaku Gothic Antique': 'ゴシック',
  'IBM Plex Sans JP': 'ゴシック',
  'LINE Seed JP': 'ゴシック',
  'M PLUS 2': 'ゴシック',
  'M PLUS 1p': 'ゴシック',
  'M PLUS U': 'ゴシック',
  'M PLUS 1 Code': 'ゴシック',
  'BIZ UDPGothic': 'ゴシック',
  'Sawarabi Gothic': 'ゴシック',
  Kosugi: 'ゴシック',
  // 明朝
  'Noto Serif JP': '明朝',
  'Shippori Mincho': '明朝',
  'Shippori Mincho B1': '明朝',
  'Zen Old Mincho': '明朝',
  'Zen Antique': '明朝',
  'Zen Antique Soft': '明朝',
  'Shippori Antique': '明朝',
  'Shippori Antique B1': '明朝',
  'BIZ UDPMincho': '明朝',
  'Sawarabi Mincho': '明朝',
  'Hina Mincho': '明朝',
  'New Tegomin': '明朝',
  // 丸ゴシック
  'M PLUS Rounded 1c': '丸ゴシック',
  'Zen Maru Gothic': '丸ゴシック',
  'Kosugi Maru': '丸ゴシック',
  'Kiwi Maru': '丸ゴシック',
  // 手書き
  'Klee One': '手書き',
  'Yusei Magic': '手書き',
  Yomogi: '手書き',
  'Zen Kurenaido': '手書き',
  'Hachi Maru Pop': '手書き',
  'Yuji Boku': '手書き',
  'Yuji Mai': '手書き',
  'Yuji Syuku': '手書き',
  // デザイン
  'Dela Gothic One': 'デザイン',
  'Reggae One': 'デザイン',
  'Mochiy Pop P One': 'デザイン',
  'RocknRoll One': 'デザイン',
  'Rampart One': 'デザイン',
  'Train One': 'デザイン',
  'Potta One': 'デザイン',
  Stick: 'デザイン',
  DotGothic16: 'デザイン',
  'WDXL Lubrifont JP N': 'デザイン',
  'Kaisei Decol': 'デザイン',
  'Kaisei HarunoUmi': 'デザイン',
  'Kaisei Opti': 'デザイン',
  'Kaisei Tokumin': 'デザイン',
  // 欧文
  Anton: '欧文',
  'Bebas Neue': '欧文',
};

/** 各フォントの特徴(UI のフォント選択とプロンプトで共有)。 */
export const CAPTION_FONT_DESCRIPTIONS: Record<CaptionFontFamilyName, string> = {
  // ゴシック
  'Noto Sans JP': '定番の角ゴシック。汎用・情報的',
  'Zen Kaku Gothic New': 'モダンな細身ゴシック。洗練',
  'Zen Kaku Gothic Antique': 'やや古風な角ゴシック。落ち着き・レトロ',
  'IBM Plex Sans JP': '端正な角ゴシック。テック・企業向け',
  'LINE Seed JP': '字面が大きくくっきり。ポップ・視認性重視',
  'M PLUS 2': 'M PLUS 1p より字幅の広い角ゴシック。読みやすい',
  'M PLUS 1p': 'プロポーショナル字送りの角ゴシック。文章向き',
  'M PLUS U': 'やわらかい角ゴシック。親しみ・UI 向き',
  'M PLUS 1 Code': '等幅の角ゴシック。数字・データの表示向き',
  'BIZ UDPGothic': '読みやすい UD ゴシック。説明・字幕向き',
  'Sawarabi Gothic': '線の細い和文ゴシック。控えめ・軽い',
  Kosugi: 'シンプルな角ゴシック。素朴・軽量',
  // 明朝
  'Noto Serif JP': '上品な明朝。落ち着き・信頼感',
  'Shippori Mincho': 'クラシックな明朝。和・伝統・高級感',
  'Shippori Mincho B1': 'Shippori Mincho より太い明朝。見出し向き',
  'Zen Old Mincho': '古典的な明朝。重厚・ドキュメンタリー向き',
  'Zen Antique': 'レトロな明朝。昭和・ノスタルジー',
  'Zen Antique Soft': 'Zen Antique をやわらげた明朝。レトロで穏やか',
  'Shippori Antique': 'かなが太いアンチック体。マンガのセリフ風',
  'Shippori Antique B1': 'さらに太いアンチック体。強いセリフ・インパクト',
  'BIZ UDPMincho': '読みやすい UD 明朝。説明・字幕向き',
  'Sawarabi Mincho': '軽やかな明朝。上品・控えめ',
  'Hina Mincho': '極細の明朝。繊細・上質',
  'New Tegomin': '筆味のある明朝。和風・手紙風',
  // 丸ゴシック
  'M PLUS Rounded 1c': '丸ゴシック。親しみ・やさしい',
  'Zen Maru Gothic': 'やわらかい丸ゴシック。カジュアル',
  'Kosugi Maru': '素朴な丸ゴシック。軽い・親しみ',
  'Kiwi Maru': 'やや細い丸ゴシック。やさしい・レトロ',
  // 手書き
  'Klee One': '手書き風の硬筆体。ナチュラル・温かみ',
  'Yusei Magic': 'マーカー手書き風。元気・ポップ',
  Yomogi: 'ゆるい手書き。カジュアル・日常',
  'Zen Kurenaido': '硬筆の手書き風。ノート・素朴',
  'Hachi Maru Pop': '丸い手書きポップ。かわいい・ゆるい',
  'Yuji Boku': '素朴な毛筆。和・味わい',
  'Yuji Mai': '流れる毛筆。和・情緒',
  'Yuji Syuku': '端正な毛筆。和・格式',
  // デザイン
  'Dela Gothic One': '極太の見出しゴシック。広告・インパクト(YouTube テロップの定番)',
  'Reggae One': '太い筆致の見出し書体。勢い・バラエティ',
  'Mochiy Pop P One': '丸くポップな見出し。かわいい・キッズ向け (字詰め版)',
  'RocknRoll One': '太めの丸ゴシック見出し。元気・ポップ',
  'Rampart One': '立体的な袋文字。飛び出す見出し・遊び',
  'Train One': '角ばった極太見出し。力強い・スポーツ',
  'Potta One': 'ぽってり太い見出し。かわいい・にぎやか',
  Stick: '細い直線的な書体。シンプル・スタイリッシュ',
  DotGothic16: 'ドット文字。レトロゲーム・8bit 風',
  'WDXL Lubrifont JP N': '縦長で詰まった見出し。文字数の多いテロップ向き',
  'Kaisei Decol': '丸みのある明朝系見出し。やわらかい・レトロ',
  'Kaisei HarunoUmi': '穏やかな明朝系見出し。上品・落ち着き',
  'Kaisei Opti': '読みやすい明朝系見出し。標準的・汎用',
  'Kaisei Tokumin': '太い明朝系見出し。重厚・インパクト',
  // 欧文
  Anton: '欧文の極太コンデンス。英字の強い訴求(日本語には使わない)',
  'Bebas Neue': '欧文のコンデンス見出し。モダン(日本語には使わない)',
};

/**
 * 各フォントが提供する数値ウェイト (Google Fonts の実データ。@remotion/google-fonts の
 * getInfo().fonts.normal のキーと一致)。UI のウェイト一覧・AI プロンプト・丸めに使う。
 * 1 ウェイトしかない見出し書体は合成太字にせず、そのウェイトだけを使う。
 */
export const CAPTION_FONT_AVAILABLE_WEIGHTS: Record<CaptionFontFamilyName, readonly number[]> = {
  // ゴシック
  'Noto Sans JP': [100, 200, 300, 400, 500, 600, 700, 800, 900],
  'Zen Kaku Gothic New': [300, 400, 500, 700, 900],
  'Zen Kaku Gothic Antique': [300, 400, 500, 700, 900],
  'IBM Plex Sans JP': [100, 200, 300, 400, 500, 600, 700],
  'LINE Seed JP': [100, 400, 700, 800],
  'M PLUS 2': [100, 200, 300, 400, 500, 600, 700, 800, 900],
  'M PLUS 1p': [100, 300, 400, 500, 700, 800, 900],
  'M PLUS U': [100, 200, 300, 400, 500, 600, 700, 800, 900],
  'M PLUS 1 Code': [100, 200, 300, 400, 500, 600, 700],
  'BIZ UDPGothic': [400, 700],
  'Sawarabi Gothic': [400],
  Kosugi: [400],
  // 明朝
  'Noto Serif JP': [200, 300, 400, 500, 600, 700, 800, 900],
  'Shippori Mincho': [400, 500, 600, 700, 800],
  'Shippori Mincho B1': [400, 500, 600, 700, 800],
  'Zen Old Mincho': [400, 500, 600, 700, 900],
  'Zen Antique': [400],
  'Zen Antique Soft': [400],
  'Shippori Antique': [400],
  'Shippori Antique B1': [400],
  'BIZ UDPMincho': [400, 700],
  'Sawarabi Mincho': [400],
  'Hina Mincho': [400],
  'New Tegomin': [400],
  // 丸ゴシック
  'M PLUS Rounded 1c': [100, 300, 400, 500, 700, 800, 900],
  'Zen Maru Gothic': [300, 400, 500, 700, 900],
  'Kosugi Maru': [400],
  'Kiwi Maru': [300, 400, 500],
  // 手書き
  'Klee One': [400, 600],
  'Yusei Magic': [400],
  Yomogi: [400],
  'Zen Kurenaido': [400],
  'Hachi Maru Pop': [400],
  'Yuji Boku': [400],
  'Yuji Mai': [400],
  'Yuji Syuku': [400],
  // デザイン
  'Dela Gothic One': [400],
  'Reggae One': [400],
  'Mochiy Pop P One': [400],
  'RocknRoll One': [400],
  'Rampart One': [400],
  'Train One': [400],
  'Potta One': [400],
  Stick: [400],
  DotGothic16: [400],
  'WDXL Lubrifont JP N': [400],
  'Kaisei Decol': [400, 500, 700],
  'Kaisei HarunoUmi': [400, 500, 700],
  'Kaisei Opti': [400, 500, 700],
  'Kaisei Tokumin': [400, 500, 700, 800],
  // 欧文
  Anton: [400],
  'Bebas Neue': [400],
};

/**
 * 各フォントの静的 (static) 版の PostScript ファミリー名。Premiere Pro の XML (xmeml) の
 * Text ジェネレータは fontname を PostScript 名で照合するため、表示名 ("Noto Sans JP") の
 * ままだと未解決フォントになる。
 *
 * 値は配布 TTF の name テーブル (nameID 6) から実測したもので、ファミリー名から機械的に
 * 空白を除いた形とは限らない。実際 M PLUS Rounded 1c はファイル名が MPLUSRounded1c-*.ttf
 * なのに PostScript 名は RoundedMplus1c-* で、推定で書くと Premiere が解決できない。
 * 書体を追加したら必ず実フォントから読み取って埋めること。
 */
export const CAPTION_FONT_POSTSCRIPT_FAMILY: Record<CaptionFontFamilyName, string> = {
  // ゴシック
  'Noto Sans JP': 'NotoSansJP',
  'Zen Kaku Gothic New': 'ZenKakuGothicNew',
  'Zen Kaku Gothic Antique': 'ZenKakuGothicAntique',
  'IBM Plex Sans JP': 'IBMPlexSansJP',
  'LINE Seed JP': 'LINESeedJP',
  'M PLUS 2': 'MPLUS2',
  'M PLUS 1p': 'MPLUS1p',
  'M PLUS U': 'MPLUSU',
  'M PLUS 1 Code': 'MPLUS1Code',
  'BIZ UDPGothic': 'BIZUDPGothic',
  'Sawarabi Gothic': 'SawarabiGothic',
  Kosugi: 'Kosugi',
  // 明朝
  'Noto Serif JP': 'NotoSerifJP',
  'Shippori Mincho': 'ShipporiMincho',
  'Shippori Mincho B1': 'ShipporiMinchoB1',
  'Zen Old Mincho': 'ZenOldMincho',
  'Zen Antique': 'ZenAntique',
  'Zen Antique Soft': 'ZenAntiqueSoft',
  'Shippori Antique': 'ShipporiAntique',
  'Shippori Antique B1': 'ShipporiAntiqueB1',
  'BIZ UDPMincho': 'BIZUDPMincho',
  'Sawarabi Mincho': 'SawarabiMincho',
  'Hina Mincho': 'HinaMincho',
  'New Tegomin': 'NewTegomin',
  // 丸ゴシック
  'M PLUS Rounded 1c': 'RoundedMplus1c',
  'Zen Maru Gothic': 'ZenMaruGothic',
  'Kosugi Maru': 'KosugiMaru',
  'Kiwi Maru': 'KiwiMaru',
  // 手書き
  'Klee One': 'KleeOne',
  'Yusei Magic': 'YuseiMagic',
  Yomogi: 'Yomogi',
  'Zen Kurenaido': 'ZenKurenaido',
  'Hachi Maru Pop': 'HachiMaruPop',
  'Yuji Boku': 'YujiBoku',
  'Yuji Mai': 'YujiMai',
  'Yuji Syuku': 'YujiSyuku',
  // デザイン
  'Dela Gothic One': 'DelaGothicOne',
  'Reggae One': 'ReggaeOne',
  'Mochiy Pop P One': 'MochiyPopPOne',
  'RocknRoll One': 'RocknRollOne',
  'Rampart One': 'RampartOne',
  'Train One': 'TrainOne',
  'Potta One': 'PottaOne',
  Stick: 'Stick',
  DotGothic16: 'DotGothic16',
  'WDXL Lubrifont JP N': 'WDXLLubrifontJPN',
  'Kaisei Decol': 'KaiseiDecol',
  'Kaisei HarunoUmi': 'KaiseiHarunoUmi',
  'Kaisei Opti': 'KaiseiOpti',
  'Kaisei Tokumin': 'KaiseiTokumin',
  // 欧文
  Anton: 'Anton',
  'Bebas Neue': 'BebasNeue',
};

/**
 * フォント ZIP のファイル名に使う名前 (Google Fonts が配る静的 TTF のファイル名の族部分)。
 * build_caption_font_packs.py がこの名前で ZIP を作り公開 GCS に置くので、PostScript 名とは
 * 別に持つ (両者が食い違うのは M PLUS Rounded 1c のみ)。
 */
export const CAPTION_FONT_PACK_NAME: Record<CaptionFontFamilyName, string> = {
  // ゴシック
  'Noto Sans JP': 'NotoSansJP',
  'Zen Kaku Gothic New': 'ZenKakuGothicNew',
  'Zen Kaku Gothic Antique': 'ZenKakuGothicAntique',
  'IBM Plex Sans JP': 'IBMPlexSansJP',
  'LINE Seed JP': 'LINESeedJP',
  'M PLUS 2': 'MPLUS2',
  'M PLUS 1p': 'MPLUS1p',
  'M PLUS U': 'MPLUSU',
  'M PLUS 1 Code': 'MPLUS1Code',
  'BIZ UDPGothic': 'BIZUDPGothic',
  'Sawarabi Gothic': 'SawarabiGothic',
  Kosugi: 'Kosugi',
  // 明朝
  'Noto Serif JP': 'NotoSerifJP',
  'Shippori Mincho': 'ShipporiMincho',
  'Shippori Mincho B1': 'ShipporiMinchoB1',
  'Zen Old Mincho': 'ZenOldMincho',
  'Zen Antique': 'ZenAntique',
  'Zen Antique Soft': 'ZenAntiqueSoft',
  'Shippori Antique': 'ShipporiAntique',
  'Shippori Antique B1': 'ShipporiAntiqueB1',
  'BIZ UDPMincho': 'BIZUDPMincho',
  'Sawarabi Mincho': 'SawarabiMincho',
  'Hina Mincho': 'HinaMincho',
  'New Tegomin': 'NewTegomin',
  // 丸ゴシック
  'M PLUS Rounded 1c': 'MPLUSRounded1c',
  'Zen Maru Gothic': 'ZenMaruGothic',
  'Kosugi Maru': 'KosugiMaru',
  'Kiwi Maru': 'KiwiMaru',
  // 手書き
  'Klee One': 'KleeOne',
  'Yusei Magic': 'YuseiMagic',
  Yomogi: 'Yomogi',
  'Zen Kurenaido': 'ZenKurenaido',
  'Hachi Maru Pop': 'HachiMaruPop',
  'Yuji Boku': 'YujiBoku',
  'Yuji Mai': 'YujiMai',
  'Yuji Syuku': 'YujiSyuku',
  // デザイン
  'Dela Gothic One': 'DelaGothicOne',
  'Reggae One': 'ReggaeOne',
  'Mochiy Pop P One': 'MochiyPopPOne',
  'RocknRoll One': 'RocknRollOne',
  'Rampart One': 'RampartOne',
  'Train One': 'TrainOne',
  'Potta One': 'PottaOne',
  Stick: 'Stick',
  DotGothic16: 'DotGothic16',
  'WDXL Lubrifont JP N': 'WDXLLubrifontJPN',
  'Kaisei Decol': 'KaiseiDecol',
  'Kaisei HarunoUmi': 'KaiseiHarunoUmi',
  'Kaisei Opti': 'KaiseiOpti',
  'Kaisei Tokumin': 'KaiseiTokumin',
  // 欧文
  Anton: 'Anton',
  'Bebas Neue': 'BebasNeue',
};

/** 数値ウェイト → 静的フォントのスタイル名 (PostScript 名の接尾辞)。 */
const POSTSCRIPT_STYLE_NAMES: Record<number, string> = {
  100: 'Thin',
  200: 'ExtraLight',
  300: 'Light',
  400: 'Regular',
  500: 'Medium',
  600: 'SemiBold',
  700: 'Bold',
  800: 'ExtraBold',
  900: 'Black',
};

/** design.fontWeight の入力。旧データの "normal"/"bold" と数値ウェイト (100〜900) の両方を受ける。 */
export type CaptionFontWeightInput = 'normal' | 'bold' | number;

export const DEFAULT_CAPTION_FONT_FAMILY: CaptionFontFamilyName = 'Noto Sans JP';
export const DEFAULT_CAPTION_FONT_WEIGHT = 400;

/**
 * fontWeight をそのフォントが実際に提供するウェイトに丸める。
 * "normal"=400 / "bold"=700 として扱い、無ければ最も近いウェイト (同距離なら太い方) に倒す。
 */
export function resolveCaptionFontWeight(
  family: CaptionFontFamilyName,
  weight: CaptionFontWeightInput | null | undefined,
): number {
  const available = CAPTION_FONT_AVAILABLE_WEIGHTS[family];
  const wanted =
    weight === 'bold' ? 700 : typeof weight === 'number' && Number.isFinite(weight) ? weight : 400;
  return available.reduce((best, w) => {
    const dw = Math.abs(w - wanted);
    const db = Math.abs(best - wanted);
    return dw < db || (dw === db && w > best) ? w : best;
  }, available[0]!);
}

/**
 * フォントと fontWeight から静的フォントの PostScript 名 (例: "NotoSansJP-Bold") を返す。
 * ウェイトは resolveCaptionFontWeight でそのフォントが実際に提供するものに丸めるので、
 * 1 ウェイトしか無い見出し書体は常に "-Regular" になる。
 * Premiere Pro 用 XML の fontname に使う (FCPXML はファミリー名 + fontFace で照合するので不要)。
 */
export function resolveCaptionFontPostScriptName(
  family: CaptionFontFamilyName,
  weight: CaptionFontWeightInput | null | undefined,
): string {
  const resolved = resolveCaptionFontWeight(family, weight);
  return `${CAPTION_FONT_POSTSCRIPT_FAMILY[family]}-${POSTSCRIPT_STYLE_NAMES[resolved] ?? 'Regular'}`;
}

/**
 * 書体ごとのフォント ZIP (静的 TTF 全ウェイト + ライセンス + README.txt) の配信元。
 * scripts/fonts/build_caption_font_packs.py が作って公開 GCS に置く。ZIP 名は PostScript
 * ファミリー名 (例: NotoSansJP.zip)。Premiere / Final Cut で XML を開くユーザーが、エディタと
 * 同じフォントを端末に入れるためのもの (書き出し UI の「フォント」と書き出し ZIP の README が案内)。
 */
export const CAPTION_FONT_PACK_BASE_URL: string =
  (import.meta.env.VITE_CAPTION_FONT_PACK_BASE_URL as string | undefined)?.replace(/\/+$/, '') ||
  'https://storage.googleapis.com/show-hin-public/caption-fonts';

/** 書体のフォント ZIP のダウンロード URL。 */
export function captionFontPackUrl(family: CaptionFontFamilyName): string {
  return `${CAPTION_FONT_PACK_BASE_URL}/${CAPTION_FONT_PACK_NAME[family]}.zip`;
}

/** 書体のフォント ZIP のファイル名 (download 属性・案内文用)。 */
export function captionFontPackFilename(family: CaptionFontFamilyName): string {
  return `${CAPTION_FONT_PACK_NAME[family]}.zip`;
}

/**
 * 旧スキーマ(種別指定)および旧許可リスト (macOS 標準フォント名) からの互換マッピング。
 * 保存済みプロジェクトの design.fontFamily をそのまま描画できるようにする。
 */
export const LEGACY_FAMILY_MAP: Record<string, CaptionFontFamilyName> = {
  gothic: 'Noto Sans JP',
  mincho: 'Noto Serif JP',
  rounded: 'M PLUS Rounded 1c',
  'Hiragino Sans': 'Noto Sans JP',
  'Hiragino Mincho ProN': 'Noto Serif JP',
  'Hiragino Maru Gothic ProN': 'M PLUS Rounded 1c',
  YuGothic: 'Zen Kaku Gothic New',
  YuMincho: 'Shippori Mincho',
  'Toppan Bunkyu Midashi Gothic': 'Dela Gothic One',
  'Toppan Bunkyu Midashi Mincho': 'Shippori Mincho',
  'Tsukushi A Round Gothic': 'Zen Maru Gothic',
  Klee: 'Klee One',
  Impact: 'Anton',
  Futura: 'Bebas Neue',
  // 等幅版はプロポーショナル版に一本化 (2026-09-15、計画書 §2.0)。
  'M PLUS 1': 'M PLUS 1p',
  'BIZ UDGothic': 'BIZ UDPGothic',
  'BIZ UDMincho': 'BIZ UDPMincho',
  'Mochiy Pop One': 'Mochiy Pop P One',
};

/**
 * design.fontFamily の値を許可リストのフォント名に解決する。
 * 旧種別("gothic" 等)・旧フォント名・未知の値は既定 (Noto Sans JP) に安全に倒す。
 */
export function resolveCaptionFontFamily(family: string): CaptionFontFamilyName {
  const mapped = LEGACY_FAMILY_MAP[family] ?? family;
  return (CAPTION_FONT_FAMILIES as readonly string[]).includes(mapped)
    ? (mapped as CaptionFontFamilyName)
    : DEFAULT_CAPTION_FONT_FAMILY;
}

/**
 * CSS の font-family スタックを組み立てる。先頭は実際に読み込んだ Web フォントの登録名
 * (captionFontLoader が返す値。M PLUS Rounded 1c は "M PLUS Rounded Onec" として登録される等、
 * 許可リストの表示名と一致しないことがあるため引数で受け取る)。
 * 読み込み失敗時のフォールバックとして、コンテナ同梱 / OS の日本語ゴシックに落ちる。
 */
export function buildFontStack(loadedFamily: string): string {
  return `"${loadedFamily}", "Noto Sans JP", "Noto Sans CJK JP", "Hiragino Sans", sans-serif`;
}
